import { FilespaceVersion } from '@lucidlink/dto/validation';
import { assertCanHandle, isNil } from '@lucidlink/tscore';
import { Logging } from '#common/logging';
import { createAesCrypto, createBase64RsaCrypto, createRsaCrypto, RSA_DEFAULT_SCHEME } from '#crypto/node-forge';
import { createClientWasm } from '#wasm/modules';
import { LOST_AND_FOUND_MIN_FILESPACE_VERSION, } from './types/init-params';
export const createClientSideFsInitializer = () => {
    return new ClientSideFilespaceInitializerImpl();
};
export const MAX_FILESPACE_VERSION = FilespaceVersion.V13;
class ClientSideFilespaceInitializerImpl {
    log = Logging.getLogger('ClientSideInitializer');
    wasm = undefined;
    async prepareInitParams(filespaceAdminPublicKey, unencryptedWorkspaceCommonKey, filespace) {
        try {
            if (filespace?.version && filespace.version > MAX_FILESPACE_VERSION) {
                throw new Error(`Unsupported filespace version: ${filespace.version}`);
            }
            // The fsVersion/format is not known for all code paths, use the feature flag to determine what to load
            this.wasm = await createClientWasm();
            return this.doPrepareInitParams(filespaceAdminPublicKey, unencryptedWorkspaceCommonKey, filespace);
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof Error);
            this.log.error({ errorMessage: err.message }, 'Failed to prepare init params.');
            throw err;
        }
        finally {
            this.tearDown();
        }
    }
    doPrepareInitParams(filespaceAdminPublicKey, unencryptedWorkspaceCommonKey, filespace) {
        const aesCrypto = createAesCrypto();
        const filespaceAdminCrypto = createBase64RsaCrypto(createRsaCrypto({ scheme: RSA_DEFAULT_SCHEME, serializedPublicKey: filespaceAdminPublicKey }));
        const { rootFwdKey, rootBwdKey, commonConfigKey, lnfData } = this.generateCryptoMaterial(unencryptedWorkspaceCommonKey, aesCrypto, filespaceAdminPublicKey, filespace);
        return {
            encryptedRootFolderAccess: this.prepareRootFolderAccess(rootFwdKey, rootBwdKey, filespaceAdminCrypto),
            encryptedCommonConfigKey: this.encryptClientCommonConfigKey(commonConfigKey, unencryptedWorkspaceCommonKey, aesCrypto),
            unencryptedCommonConfigKey: commonConfigKey,
            lostAndFound: lnfData,
        };
    }
    generateCryptoMaterial(workspaceCommonKey, aesCrypto, filespaceAdminPublicKey, filespace) {
        const rootFwdKey = aesCrypto.generateKey();
        const rootBwdKey = aesCrypto.generateKey();
        const commonConfigKey = aesCrypto.generateKey();
        // The rescue tree exists exactly on event-stream filespaces (V13+): the hub arms its check
        // constraints there, and rejected entries get rescued into the tree.
        const lnfData = !isNil(filespace) && filespace.version >= LOST_AND_FOUND_MIN_FILESPACE_VERSION
            ? {
                ...this.makeLostAndFoundInitData(rootFwdKey, rootBwdKey, workspaceCommonKey, aesCrypto, filespace.version),
                filespaceAdminPublicKey,
            }
            : undefined;
        return { rootFwdKey, rootBwdKey, commonConfigKey, lnfData };
    }
    // Only the creator holds the root folder keys and the workspace common key, so both the tree
    // crypto material and the encrypted crypto key setting must be built here. The rows themselves
    // are assembled where the tree is committed.
    makeLostAndFoundInitData(rootFwdKey, rootBwdKey, workspaceCommonKey, aesCrypto, fsVersion) {
        const lnf = this.wasm.keyValueHelpers().makeLostAndFoundInitCryptoMaterial(rootFwdKey, rootBwdKey, fsVersion);
        const keyData = this.wasm.tuple().makeTupleFromString(this.wasm.keyValueHelpers().makeRestrictedDirCryptoKeyData(lnf.dirCryptoKeys.fwd, lnf.dirCryptoKeys.bwd));
        const encrypted = aesCrypto.encrypt(keyData, workspaceCommonKey);
        const encryptedCryptoKey = this.wasm.tuple().makeTupleFromStringArray([encrypted.value, encrypted.iv]);
        return {
            dirNameHmac: lnf.dirNameHmac,
            dirFwdValue: lnf.dirFwdValue,
            subdirNameHmac: lnf.subdirNameHmac,
            subdirFwdValue: lnf.subdirFwdValue,
            encryptedCryptoKey,
        };
    }
    encryptClientCommonConfigKey(commonConfigKey, workspaceCommonKey, aesCrypto) {
        const serializedUnencrypted = this.wasm.tuple().makeTupleFromString(commonConfigKey);
        const encryptedCommonConfigKey = aesCrypto.encrypt(serializedUnencrypted, workspaceCommonKey);
        return this.wasm.tuple().makeTupleFromStringArray([
            encryptedCommonConfigKey.value,
            encryptedCommonConfigKey.iv,
        ]);
    }
    prepareRootFolderAccess(rootFwdKey, rootBwdKey, filespaceAdminCrypto) {
        const kvHelper = this.wasm.keyValueHelpers();
        const value = kvHelper.makeFullRootAccessValue(rootFwdKey, rootBwdKey);
        const tuple = this.wasm.tuple();
        const encryptedValues = filespaceAdminCrypto.encryptLong(value);
        const encryptedTuple = tuple.makeTupleFromBytesArray(encryptedValues);
        return encryptedTuple;
    }
    tearDown() {
        this.log.debug(`Tearing down...`);
        if (this.wasm) {
            this.wasm.tearDown();
            this.wasm = undefined;
        }
        this.log.debug('Done');
    }
}
